import { useCallback, useEffect, useRef, useState } from 'react';
import type { RemoteLoadRequest, RemoteModule, RemoteRouteManifestEntry } from './types';
import { loadRemoteModule } from './remoteLoader';
import { RemoteRouteFallback } from './RemoteRouteFallback';
import { RemoteRouteErrorBoundary } from './RemoteRouteErrorBoundary';
import { publishRemoteObservabilityEvent } from './remoteTelemetry';
import { resolveRemoteRoute } from './routeManifest';

interface RemoteRouteMountProps {
  routePath: string;
  resolver?: (pathname: string) => RemoteRouteManifestEntry | null;
  remoteLoader?: (request: RemoteLoadRequest) => Promise<RemoteModule>;
}

function createCorrelationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `corr-${Date.now()}`;
}

export function RemoteRouteMount({
  routePath,
  resolver = resolveRemoteRoute,
  remoteLoader = loadRemoteModule,
}: RemoteRouteMountProps) {
  const [viewState, setViewState] = useState<{
    isLoading: boolean;
    remoteModule: RemoteModule | null;
    errorMessage: string | null;
  }>({
    isLoading: true,
    remoteModule: null,
    errorMessage: null,
  });
  const [correlationId, setCorrelationId] = useState(createCorrelationId);
  const previousRoutePath = useRef(routePath);

  const route = resolver(routePath);

  useEffect(() => {
    if (previousRoutePath.current !== routePath) {
      previousRoutePath.current = routePath;
      setCorrelationId(createCorrelationId());
    }
  }, [routePath]);

  const handleRetry = useCallback(() => {
    setCorrelationId(createCorrelationId());
  }, []);

  useEffect(() => {
    if (!route) {
      return;
    }

    let isActive = true;
    const loadStartedAt = Date.now();

    setViewState({
      isLoading: true,
      remoteModule: null,
      errorMessage: null,
    });

    publishRemoteObservabilityEvent({
      stage: 'remote_load_start',
      remoteName: route.remoteName,
      routePath: route.routePath,
      moduleId: route.moduleId,
      correlationId,
    });

    remoteLoader({
      remoteName: route.remoteName,
      routePath: route.routePath,
      moduleId: route.moduleId,
      timeoutMs: route.timeoutMs ?? 5000,
      correlationId,
    })
      .then((module) => {
        if (isActive) {
          publishRemoteObservabilityEvent({
            stage: 'remote_load_success',
            remoteName: route.remoteName,
            routePath: route.routePath,
            moduleId: route.moduleId,
            correlationId,
            durationMs: Date.now() - loadStartedAt,
          });
          setViewState({
            isLoading: false,
            remoteModule: module,
            errorMessage: null,
          });
        }
      })
      .catch((error: unknown) => {
        if (isActive) {
          const message = error instanceof Error ? error.message : 'Remote load failed';
          publishRemoteObservabilityEvent({
            stage: 'remote_load_failure',
            remoteName: route.remoteName,
            routePath: route.routePath,
            moduleId: route.moduleId,
            correlationId,
            durationMs: Date.now() - loadStartedAt,
            errorMessage: message,
          });
          setViewState({
            isLoading: false,
            remoteModule: null,
            errorMessage: message,
          });
        }
      });

    return () => {
      isActive = false;
    };
  }, [correlationId, remoteLoader, route]);

  if (!route) {
    return null;
  }

  if (viewState.isLoading) {
    return (
      <div className="min-h-[30vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  const fallback = (
    <RemoteRouteFallback
      metadata={{
        remoteName: route.remoteName,
        routePath: route.routePath,
        correlationId,
      }}
      message={viewState.errorMessage ?? 'The remote route could not be loaded.'}
      onRetry={handleRetry}
    />
  );

  if (viewState.errorMessage || !viewState.remoteModule) {
    return fallback;
  }

  const RemoteComponent = viewState.remoteModule.default;

  return (
    <RemoteRouteErrorBoundary
      resetKey={correlationId}
      onError={(error) => {
        publishRemoteObservabilityEvent({
          stage: 'remote_render_failure',
          remoteName: route.remoteName,
          routePath: route.routePath,
          moduleId: route.moduleId,
          correlationId,
          errorMessage: error.message,
        });
        setViewState({
          isLoading: false,
          remoteModule: null,
          errorMessage: error.message,
        });
      }}
      fallback={fallback}
    >
      <RemoteComponent />
    </RemoteRouteErrorBoundary>
  );
}
